import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { logConsentAcknowledgement } from '@/lib/consentLogger';
import { acquireJobLock, releaseJobLock } from '@/lib/jobLockManager';
import { HireCvScoringError, readHireCvScoringFunctionError } from '@/lib/hireCvScoringErrors';
import { classifyCvProcessingFailure, cvProcessingFailureTranslationBase } from '@/lib/forkhire/cvProcessingFailure';
import { useTranslation } from 'react-i18next';

const TRANSPORT_ERROR_PATTERNS = [
  'Failed to fetch',
  'Failed to send a request to the Edge Function',
  'network',
  'connection closed before message completed',
];

const isTransportError = (message: string) =>
  TRANSPORT_ERROR_PATTERNS.some((pattern) =>
    message.toLowerCase().includes(pattern.toLowerCase())
  );

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function getExistingScoreResult(applicationId: string) {
  const { data, error } = await supabase
    .from('cv_scores')
    .select('total_score')
    .eq('application_id', applicationId)
    .maybeSingle();

  if (error || !data) return null;

  return {
    success: true,
    score: data.total_score,
    total_score: data.total_score,
    breakdown: {},
    explanation: null,
    hard_blockers: [],
    document_type_valid: undefined,
    detected_document_type: undefined,
    rejection_reason: undefined,
    recovered_from_transport_error: true,
  };
}

export interface DuplicateInfo {
  existing_candidate_id: string;
  existing_candidate_name: string;
  existing_candidate_email: string;
  existing_application_id: string;
  existing_application_stage: string;
  existing_application_status: string;
  existing_cv_score: number | null;
  new_candidate_name: string;
  new_candidate_email: string;
  new_application_id: string;
}

export function useScoreCv() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  const scoringError = async (error: unknown) => {
    const parsed = await readHireCvScoringFunctionError(error);
    const kind = classifyCvProcessingFailure({
      code: parsed.code,
      retryable: parsed.retryable,
      documentTypeValid: parsed.documentTypeValid,
    });
    const fallback = kind
      ? t(`${cvProcessingFailureTranslationBase(kind)}.description`)
      : t('hire.cvScoring.errors.scoringFailed');
    return new HireCvScoringError(parsed, fallback);
  };

  const controlledDuplicateResult = async (error: unknown) => {
    const parsed = await readHireCvScoringFunctionError(error);
    if (parsed.duplicateFound && parsed.duplicate) {
      return {
        success: false,
        duplicate_found: true,
        duplicate: parsed.duplicate,
      };
    }
    return null;
  };

  return useMutation({
    mutationFn: async (applicationId: string) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const jobKey = `score-cv:${applicationId}`;
      let lockId: string | null = null;

      try {
        // Acquire lock
        const lockResult = await acquireJobLock(jobKey, 'score-cv', session.user.id, 'hire', 300);

        if (!lockResult.acquired) {
          if (lockResult.reuse_result && lockResult.result_json) {
            return lockResult.result_json;
          }
          if (lockResult.reconnected) {
            throw new Error('CV scoring is already in progress for this application. Please wait.');
          }
        }

        lockId = lockResult.lock_id;

        // Log consent
        logConsentAcknowledgement('cv_scoring', session.user.id, 'hire', { application_id: applicationId }).catch(() => {});

        const invokeScore = () =>
          supabase.functions.invoke('score-cv', {
            body: { application_id: applicationId },
          });

        const res = await invokeScore();

        if (res.error) {
          const duplicateResult = await controlledDuplicateResult(res.error);
          if (duplicateResult) {
            await releaseJobLock(lockId, 'completed', 'hire', duplicateResult);
            return duplicateResult;
          }
          const msg = typeof res.error === 'object' && 'message' in res.error
            ? res.error.message
            : String(res.error);

          if (isTransportError(msg || '')) {
            for (let attempt = 1; attempt <= 3; attempt++) {
              await sleep(1200 * attempt);
              const recovered = await getExistingScoreResult(applicationId);
              if (recovered) {
                await releaseJobLock(lockId, 'completed', 'hire', recovered);
                return recovered;
              }
            }

            const retry = await invokeScore();
            if (!retry.error) {
              await releaseJobLock(lockId, 'completed', 'hire', retry.data);
              return retry.data;
            }

            const retryDuplicateResult = await controlledDuplicateResult(retry.error);
            if (retryDuplicateResult) {
              await releaseJobLock(lockId, 'completed', 'hire', retryDuplicateResult);
              return retryDuplicateResult;
            }

            const retryMsg = typeof retry.error === 'object' && 'message' in retry.error
              ? retry.error.message
              : String(retry.error);

            if (isTransportError(retryMsg || '')) {
              const recovered = await getExistingScoreResult(applicationId);
              if (recovered) {
                await releaseJobLock(lockId, 'completed', 'hire', recovered);
                return recovered;
              }
            }

            throw await scoringError(retry.error || res.error);
          }

          throw await scoringError(res.error);
        }

        // Release lock as completed
        await releaseJobLock(lockId, 'completed', 'hire', res.data);
        return res.data;
      } catch (err) {
        // Release lock as failed
        if (lockId) {
          try {
            await releaseJobLock(lockId, 'failed', 'hire', null, err instanceof Error ? err.message : String(err));
          } catch { /* best effort */ }
        }
        throw err;
      }
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['candidates'] });
      qc.invalidateQueries({ queryKey: ['job-candidates'] });
      
      if (data?.document_type_valid === false) {
        toast({
          title: 'Document Rejected',
          description: `Not a valid CV/resume (detected: ${data.detected_document_type}). ${data.rejection_reason || ''}`,
          variant: 'destructive',
        });
        return;
      }

      if (data?.duplicate_found) return;

      const blockers = data?.hard_blockers ?? data?.breakdown?.hard_blockers ?? data?.breakdown_json?.hard_blockers ?? [];
      const hasBlockers = Array.isArray(blockers) && blockers.length > 0;
      const scoreValue = data?.score ?? data?.total_score ?? 0;
      toast({
        title: 'CV Scored',
        description: `Score: ${scoreValue}/100${hasBlockers ? ` | ${blockers.length} blocker(s)` : ''}`,
      });
    },
    onError: (e: Error) => {
      const kind = e instanceof HireCvScoringError
        ? classifyCvProcessingFailure({
            code: e.code,
            retryable: e.retryable,
            documentTypeValid: e.documentTypeValid,
          })
        : null;
      toast({
        title: kind
          ? t(`${cvProcessingFailureTranslationBase(kind)}.title`)
          : t('hire.cvScoring.errors.scoringFailedTitle'),
        description: e.message,
        variant: 'destructive',
      });
    },
  });
}
